import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../layout/Modal';
import { REPORT_TYPES } from './reportTypes';
import CampoMultiSeletor from '../Formulario/CampoMultiSeletor';
import FiltroConstrutor from '../Filtros/FiltroConstrutor';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { ReportBuildMetadata } from '@tera/shared/src/types/ReportBuildMetadata';
import { ReportFiltroAtivo, translateFilterGenerico, PERIODO_RELATIVO_OPTIONS, resolvePeriodoRelativo } from '../../pages/relatorios/util';
import VisualizadorRelatorio from '../../pages/relatorios/view/VisualizadorRelatorio';
import VBox from '../layout/VBox';
import HBox from '../layout/HBox';
import Grid, { GridItem } from '../layout/Grid';
import Botao from '../Botao';
import { mdiChevronLeft, mdiChevronRight, mdiClose, mdiContentSave, mdiTableLarge } from '@mdi/js';
import styles from './ModalNovoRelatorio.module.css';
import CampoTexto from '../Formulario/CampoTexto';
import CampoSeletor from '../Formulario/CampoSeletor';
import CampoData from '../Formulario/CampoData';
import AlertBox from '../layout/AlertBox';
import { RelatorioPersonalizadoDto } from '@tera/shared/src/dto/RelatorioPersonalizadoDto';
import { toast } from 'react-toastify';
import Subtitulo from '../tipografia/Subtitulo';

type Props = {
    show: boolean;
    onClose: () => void;
    /** Quando fornecido, abre em modo edição com dados pré-preenchidos */
    relatorio?: RelatorioPersonalizadoDto | null;
    /** Chamado após salvar/editar com sucesso */
    onSaved?: () => void;
};

type Step = 'tipo' | 'configurar';

const ModalNovoRelatorio = ({ show, onClose, relatorio, onSaved }: Props) => {
    const queryClient = useQueryClient();
    const [step, setStep] = useState<Step>('tipo');
    const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
    const [metricas, setMetricas] = useState<string[]>([]);
    const [dimensoes, setDimensoes] = useState<string[]>([]);
    const [filtros, setFiltros] = useState<ReportFiltroAtivo[]>([]);
    const [generated, setGenerated] = useState(false);
    const [filtroRaw, setFiltroRaw] = useState<string>('{}');
    const [metricasCommit, setMetricasCommit] = useState<string[]>([]);
    const [dimensoesCommit, setDimensoesCommit] = useState<string[]>([]);
    const [generateKey, setGenerateKey] = useState(0);

    // Campos do step salvar
    const [titulo, setTitulo] = useState('');
    const [periodoRelativo, setPeriodoRelativo] = useState<string>('mes');
    const [periodoInicio, setPeriodoInicio] = useState('');
    const [periodoFim, setPeriodoFim] = useState('');
    const [escopo, setEscopo] = useState<'privado' | 'publico'>('privado');
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [editandoId, setEditandoId] = useState<number | null>(null);

    const selectedReportType = REPORT_TYPES.find(r => r.id === selectedTypeId);

    // Pré-popula ao abrir em modo edição
    useEffect(() => {
        if (relatorio && show) {
            setEditandoId(relatorio.id);
            setSelectedTypeId(relatorio.report_type_id);
            setMetricas(relatorio.metricas);
            setDimensoes(relatorio.dimensoes);
            try { setFiltros(JSON.parse(relatorio.filtros_raw || '[]')); } catch { setFiltros([]); }
            setGenerated(false);
            setStep('configurar');
            setTitulo(relatorio.titulo);
            setPeriodoRelativo(relatorio.periodo);
            setPeriodoInicio(relatorio.periodo_inicio ?? '');
            setPeriodoFim(relatorio.periodo_fim ?? '');
            setEscopo(relatorio.escopo);
            setSaveError(null);
        }
    }, [show, relatorio?.id]);

    const { data: metadata, isLoading: loadingMetadata } = useQuery<ReportBuildMetadata>(
        ['report-metadata', selectedReportType?.href],
        async () => {
            const res = await axios.get(`${selectedReportType!.href}/metadata`);
            return res.data.result as ReportBuildMetadata;
        },
        {
            enabled: !!selectedReportType && step === 'configurar',
            staleTime: 5 * 60 * 1000,
            onSuccess: data => {
                if (!selectedReportType || relatorio) return;
                setMetricas(selectedReportType.defaultMetrics.filter(m => !!data.metrics[m]));
                setDimensoes(selectedReportType.defaultDimensions.filter(d => !!data.dimensions[d]));
            },
        }
    );

    const handleSelectType = (typeId: string) => setSelectedTypeId(typeId);

    const handleContinue = () => {
        if (!selectedTypeId) return;
        setGenerated(false);
        setFiltros([]);
        setStep('configurar');
    };

    const handleBack = () => {
        setStep('tipo');
        setGenerated(false);
        setMetricasCommit([]);
        setDimensoesCommit([]);
    };

    // Datas resolvidas do período — atualizadas reativamente (precisa estar antes de handleGenerate)
    const { dataInicio: previewInicio, dataFim: previewFim } = useMemo(
        () => resolvePeriodoRelativo(periodoRelativo, periodoInicio, periodoFim),
        [periodoRelativo, periodoInicio, periodoFim]
    );

    // Quando qualquer configuração muda após uma geração, invalida a pré-visualização
    // para forçar o usuário a re-gerar com o novo estado.
    useEffect(() => {
        if (generated) setGenerated(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtros, metricas, dimensoes, periodoRelativo, periodoInicio, periodoFim]);

    const handleGenerate = useCallback(() => {
        const TEMPORAL_DIMS = ['data_criacao', 'data_criacao_dia', 'data_criacao_semana', 'data_criacao_mes'];
        const temDimensaoTemporal = dimensoes.some(d => TEMPORAL_DIMS.includes(d));

        let filtrosParaGerar = filtros;
        if (temDimensaoTemporal) {
            // Sempre injeta o período selecionado como filtro de data
            filtrosParaGerar = [
                ...filtros,
                { field: 'data_criacao', type: 'date', operator: 'between', value: [previewInicio, previewFim] },
            ];
        }

        const raw = translateFilterGenerico(filtrosParaGerar, metadata?.filters);
        setFiltroRaw(raw);
        setMetricasCommit(metricas);
        setDimensoesCommit(dimensoes);
        setGenerated(true);
        setGenerateKey(k => k + 1);
    }, [filtros, metricas, dimensoes, metadata?.filters, previewInicio, previewFim]);

    const handleSave = async () => {
        if (!titulo.trim()) { setSaveError('O título é obrigatório.'); return; }
        if (!periodoRelativo) { setSaveError('Selecione o período.'); return; }
        if (periodoRelativo === 'custom' && (!periodoInicio || !periodoFim)) {
            setSaveError('Informe as datas de início e fim do período personalizado.'); return;
        }

        setSaving(true);
        setSaveError(null);

        // Filtros de data são removidos ao salvar — o período é controlado pelo campo `periodo`
        const filtrosSemData = filtros.filter(f => f.type !== 'date');

        const payload = {
            titulo: titulo.trim(),
            report_type_id: selectedTypeId,
            metricas,
            dimensoes,
            filtros_raw: JSON.stringify(filtrosSemData),
            periodo: periodoRelativo,
            periodo_inicio: periodoRelativo === 'custom' ? periodoInicio : null,
            periodo_fim: periodoRelativo === 'custom' ? periodoFim : null,
            escopo,
        };

        try {
            if (editandoId) {
                await axios.put(`/api/relatorio_personalizado/${editandoId}`, payload);
            } else {
                await axios.post('/api/relatorio_personalizado', payload);
            }
            queryClient.invalidateQueries('relatorio_personalizado');
            toast(editandoId ? 'Relatório atualizado!' : 'Relatório salvo!', {
                type: 'success', position: 'top-center',
            });
            onSaved?.();
            handleClose();
        } catch (err: any) {
            setSaveError(err.response?.data?.msg ?? 'Erro ao salvar relatório.');
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        setStep('tipo');
        setSelectedTypeId(null);
        setMetricas([]);
        setDimensoes([]);
        setFiltros([]);
        setGenerated(false);
        setFiltroRaw('{}');
        setMetricasCommit([]);
        setDimensoesCommit([]);
        setTitulo('');
        setPeriodoRelativo('mes');
        setPeriodoInicio('');
        setPeriodoFim('');
        setEscopo('privado');
        setSaveError(null);
        setEditandoId(null);
        onClose();
    };

    const metricasOptions = metadata?.metrics
        ? Object.values(metadata.metrics).map(m => ({ id: m.name, label: String(m.label) }))
        : [];
    const dimensoesOptions = metadata?.dimensions
        ? Object.values(metadata.dimensions).map(d => ({ id: d.name, label: String(d.label) }))
        : [];

    // Filtros de metadata sem campos do tipo date (período é controle separado)
    const filtersMetadataSemData = useMemo(() => {
        if (!metadata?.filters) return {};
        return Object.fromEntries(
            Object.entries(metadata.filters).filter(([, f]) => (f as any).type !== 'date')
        );
    }, [metadata?.filters]);
    const hasNonDateFilters = Object.keys(filtersMetadataSemData).length > 0;

    const modalTitle = step === 'tipo'
        ? (relatorio ? `Editar Relatório — ${relatorio.titulo}` : 'Novo Relatório Personalizado')
        : `${relatorio ? 'Editar' : 'Novo'} Relatório — ${selectedReportType?.label ?? ''}`;

    return (
        <Modal
            show={show}
            onClose={handleClose}
            title={modalTitle}
            size="lg"
            hideControls
        >
            <VBox gap="16px">
                {/* ── STEP 1: Tipo ── */}
                {step === 'tipo' && (
                    <>
                        <p style={{ margin: 0, color: 'var(--tc-color-secondary, #6b7280)', fontSize: '0.9rem' }}>
                            Selecione o tipo de relatório para configurar e gerar.
                        </p>
                        <VBox gap="8px">
                            {REPORT_TYPES.map(rt => (
                                <div
                                    key={rt.id}
                                    className={`${styles.typeCard} ${selectedTypeId === rt.id ? styles.typeCardSelected : ''}`}
                                    onClick={() => handleSelectType(rt.id)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => e.key === 'Enter' && handleSelectType(rt.id)}
                                >
                                    <strong className={styles.typeCardTitle}>{rt.label}</strong>
                                    <p className={styles.typeCardDesc}>{rt.description}</p>
                                </div>
                            ))}
                        </VBox>
                        <HBox style={{ justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                            <Botao variant="outline-secondary" onClick={handleClose}>
                                Cancelar
                            </Botao>
                            <Botao disabled={!selectedTypeId} onClick={handleContinue} icon={mdiChevronRight}>
                                Continuar
                            </Botao>
                        </HBox>
                    </>
                )}

                {/* ── STEP 2: Configurar ── */}
                {step === 'configurar' && selectedReportType && (
                    <>
                        {loadingMetadata ? (
                            <p style={{ color: 'var(--tc-color-secondary, #6b7280)' }}>Carregando opções…</p>
                        ) : metadata ? (
                            <>
                                <Grid>
                                    <GridItem sm={12} md={8}>
                                        <CampoTexto
                                            label="Título"
                                            placeholder="Ex: Mensagens do mês anterior por canal"
                                            value={titulo}
                                            onChange={setTitulo}
                                            error={saveError && !titulo.trim() ? 'Obrigatório' : undefined}
                                        />
                                    </GridItem>
                                    <GridItem sm={12} md={4}>
                                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: '0.875rem' }}>Visibilidade</label>
                                        <HBox gap="16px" style={{ paddingTop: 6 }}>
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <input type="radio" name="escopo" value="privado" checked={escopo === 'privado'} onChange={() => setEscopo('privado')} />
                                                <span>🔒 Somente eu</span>
                                            </label>
                                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <input type="radio" name="escopo" value="publico" checked={escopo === 'publico'} onChange={() => setEscopo('publico')} />
                                                <span>🌐 Todos</span>
                                            </label>
                                        </HBox>
                                    </GridItem>
                                </Grid>

                                <Grid>
                                    <GridItem sm={12} md={6}>
                                        <CampoMultiSeletor
                                            label="Métricas / Fatos"
                                            options={metricasOptions}
                                            value={metricas}
                                            onChange={setMetricas}
                                            placeholder="Selecionar métricas…"
                                        />
                                    </GridItem>
                                    <GridItem sm={12} md={6}>
                                        <CampoMultiSeletor
                                            label="Dimensões / Agrupamentos"
                                            options={dimensoesOptions}
                                            value={dimensoes}
                                            onChange={setDimensoes}
                                            placeholder="Selecionar dimensões…"
                                        />
                                    </GridItem>
                                </Grid>

                                {/* Período — estilo Graylog */}
                                <Grid>
                                    <GridItem sm={12} md={4}>
                                        <CampoSeletor
                                            label="Período"
                                            noSearch
                                            options={PERIODO_RELATIVO_OPTIONS.map(o => ({ id: o.id, label: o.label }))}
                                            value={periodoRelativo}
                                            onChange={v => setPeriodoRelativo(v ?? 'mes')}
                                        />
                                    </GridItem>
                                    <GridItem sm={12} md={4}>
                                        <CampoData
                                            label="De"
                                            value={periodoRelativo === 'custom' ? periodoInicio : previewInicio}
                                            onChange={periodoRelativo === 'custom' ? setPeriodoInicio : undefined}
                                            disabled={periodoRelativo !== 'custom'}
                                        />
                                    </GridItem>
                                    <GridItem sm={12} md={4}>
                                        <CampoData
                                            label="Até"
                                            value={periodoRelativo === 'custom' ? periodoFim : previewFim}
                                            onChange={periodoRelativo === 'custom' ? setPeriodoFim : undefined}
                                            disabled={periodoRelativo !== 'custom'}
                                        />
                                    </GridItem>
                                </Grid>

                                <div>
                                    <strong className={styles.sectionTitle}>Filtros</strong>
                                    {hasNonDateFilters ? (
                                        <FiltroConstrutor
                                            filtersMetadata={filtersMetadataSemData as any}
                                            reportHref={selectedReportType.href}
                                            value={filtros}
                                            onChange={setFiltros}
                                        />
                                    ) : (
                                        <span style={{ color: 'var(--tc-color-secondary, #6b7280)', fontSize: '0.875rem' }}>
                                            Este relatório não possui filtros configuráveis.
                                        </span>
                                    )}
                                </div>

                                {saveError && (
                                    <AlertBox variant="warning">
                                        <p style={{ margin: 0 }}>{saveError}</p>
                                    </AlertBox>
                                )}

                                <HBox style={{ justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
                                    <div style={{ flex: 1 }}>
                                        <Botao variant="outline-secondary" icon={mdiChevronLeft} onClick={handleBack}>
                                            {relatorio ? 'Cancelar' : 'Voltar'}
                                        </Botao>
                                    </div>
                                    <div>
                                        <HBox gap="8px">
                                            <Botao
                                                variant="outline-primary"
                                                icon={mdiTableLarge}
                                                onClick={handleGenerate}
                                                disabled={(metricas.length === 0 && dimensoes.length === 0) || generated}
                                            >
                                                Pré-visualizar
                                            </Botao>
                                            <Botao
                                                icon={mdiContentSave}
                                                onClick={handleSave}
                                                disabled={saving}
                                            >
                                                {saving ? 'Salvando…' : 'Salvar'}
                                            </Botao>
                                        </HBox>
                                    </div>
                                </HBox>

                                {generated && (
                                    <div className={styles.resultContainer}>
                                        <Subtitulo>
                                            <HBox style={{ justifyContent: 'center' }}>
                                                <span>Pré-visualização</span>
                                                <Botao variant="none-secondary" icon={mdiClose} size="sm" onClick={() => setGenerated(false)} style={{ marginLeft: 12 }}></Botao>
                                            </HBox>
                                        </Subtitulo>
                                        <VisualizadorRelatorio
                                            key={generateKey}
                                            titulo={selectedReportType.label}
                                            href={selectedReportType.href}
                                            metricas={metricasCommit}
                                            dimensoes={dimensoesCommit}
                                            filtroRaw={filtroRaw}
                                            hideAlterarDados
                                            csv
                                        />
                                    </div>
                                )}
                            </>
                        ) : null}
                    </>
                )}


            </VBox>
        </Modal>
    );
};

export default ModalNovoRelatorio;
